/* Patient Compliance Coach Chat — Phase 4 of Compliance Monitoring & Support.
 *
 * Mirror of the coach-side chat panel, but rendered for the patient with
 * a few role-aware tweaks:
 *   - Patient bubbles right-aligned (teal); coach bubbles left-aligned (card).
 *   - The welcome system bubble stays centered, same as the coach view.
 *   - When the patient has no active coach (case not opted-in, or not yet
 *     bootstrapped), we render a friendly empty-state — never a 403.
 *
 * Backend endpoints:
 *   GET  /patient/me/compliance-thread             — thread + messages + coach
 *   POST /patient/me/compliance-thread/messages    — patient posts
 */
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Heart, ChatCircleDots, PaperPlaneTilt, Clock, Sparkle, Stethoscope, Lock,
} from "@phosphor-icons/react";


function timestampLong(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString([], {
            month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        });
    } catch { return ""; }
}

function initialsOf(name = "") {
    return (name || "?").split(/\s+/).filter(Boolean).slice(0, 2)
        .map((w) => w[0]?.toUpperCase()).join("") || "?";
}

function Avatar({ name, image, size = 36, tealGradient = false }) {
    const cls = "rounded-full object-cover ring-1 ring-border";
    if (image) return <img src={image} alt={name || ""} className={cls} style={{ width: size, height: size }} />;
    const bg = tealGradient
        ? "linear-gradient(135deg, hsl(180 50% 45%), hsl(200 60% 50%))"
        : "linear-gradient(135deg, hsl(220 50% 55%), hsl(260 60% 55%))";
    return (
        <div
            className="rounded-full grid place-items-center ring-1 ring-border text-white font-display font-bold"
            style={{ width: size, height: size, fontSize: Math.max(10, size / 3), background: bg }}
        >
            {initialsOf(name)}
        </div>
    );
}


export default function PatientCoachChat() {
    const [thread, setThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [coach, setCoach] = useState(null);
    const [caseData, setCaseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);

    const load = async () => {
        try {
            const r = await api.get("/patient/me/compliance-thread");
            setThread(r.data?.thread || null);
            setMessages(r.data?.messages || []);
            setCoach(r.data?.coach || null);
            setCaseData(r.data?.case || null);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages]);

    const send = async (e) => {
        e?.preventDefault?.();
        const body = draft.trim();
        if (!body || sending) return;
        setSending(true);
        try {
            const r = await api.post("/patient/me/compliance-thread/messages", { body });
            setMessages((arr) => [...arr, r.data]);
            setDraft("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not send");
        } finally { setSending(false); }
    };

    if (loading) {
        return <div className="p-10 text-center text-sm text-muted-foreground">Loading your coach…</div>;
    }

    // No opted-in / no active case at all.
    if (!thread && !caseData) {
        return <NotEnrolledState />;
    }

    // Opted-in, but case hasn't been bootstrapped yet (not yet treatment_started
    // or no coach assigned).
    if (!thread && caseData) {
        return <PreEngagementState caseData={caseData} />;
    }

    return (
        <div className="max-w-3xl mx-auto" data-testid="patient-coach-chat">
            {/* Header */}
            <div className="flex items-center gap-3 mb-5">
                <Avatar name={coach?.name} image={coach?.image} size={48} tealGradient />
                <div>
                    <div className="flex items-center gap-2">
                        <h1 className="font-display text-xl font-semibold tracking-tight">{coach?.name || "Your coach"}</h1>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-emerald-100 text-emerald-700">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            Online
                        </span>
                    </div>
                    <div className="text-[12px] text-muted-foreground inline-flex items-center gap-1">
                        <Heart size={11} weight="fill" className="text-primary" />
                        Your Invisique™ compliance coach
                    </div>
                </div>
            </div>

            {/* Chat shell */}
            <div className="rounded-2xl border border-border bg-card overflow-hidden flex flex-col" style={{ minHeight: 540 }} data-testid="patient-coach-chat-shell">
                <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <ChatCircleDots size={15} className="text-primary" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Direct chat</span>
                    </div>
                    {thread && (
                        <span className="text-[11px] text-muted-foreground">Started {timestampLong(thread.created_at)}</span>
                    )}
                </div>

                <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-secondary/10" style={{ maxHeight: 540 }}>
                    {messages.length === 0 ? (
                        <div className="text-center text-sm text-muted-foreground py-8">No messages yet.</div>
                    ) : (
                        messages.map((m) => <MessageBubble key={m.id} msg={m} coach={coach} />)
                    )}
                </div>

                <form onSubmit={send} className="border-t border-border p-3 flex items-end gap-2 bg-card">
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                        }}
                        placeholder={`Reply to ${coach?.name?.split(/\s+/)[0] || "your coach"}…`}
                        rows={2}
                        className="flex-1 resize-none text-sm rounded-lg border border-border bg-background px-3 py-2 focus:outline-none focus:border-primary/50 focus:ring-2 focus:ring-primary/15 transition"
                        data-testid="patient-coach-chat-input"
                    />
                    <button
                        type="submit"
                        disabled={!draft.trim() || sending}
                        className="px-3 h-10 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition inline-flex items-center gap-1.5"
                        data-testid="patient-coach-chat-send"
                    >
                        <PaperPlaneTilt size={14} weight="fill" />
                        {sending ? "Sending…" : "Send"}
                    </button>
                </form>
            </div>

            <p className="text-[11px] text-muted-foreground text-center mt-4 max-w-md mx-auto leading-relaxed">
                Your coach handles day-to-day questions and wear-time guidance.
                For clinical concerns or pain, message your doctor directly via the &ldquo;Chat with doctor&rdquo; tab.
            </p>
        </div>
    );
}


function MessageBubble({ msg, coach }) {
    if (msg.sender_role === "system") {
        return (
            <div className="flex justify-center" data-testid={`patient-msg-${msg.id}`}>
                <div className="max-w-md text-center rounded-xl bg-primary/10 border border-primary/20 px-4 py-3">
                    <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-primary mb-1.5">
                        <Sparkle size={10} weight="fill" /> Welcome
                    </div>
                    <div className="text-sm text-foreground leading-relaxed">{msg.body}</div>
                    <div className="text-[10px] text-muted-foreground mt-2">{timestampLong(msg.created_at)}</div>
                </div>
            </div>
        );
    }
    const isPatient = msg.sender_role === "patient";
    return (
        <div className={`flex ${isPatient ? "justify-end" : "justify-start"}`} data-testid={`patient-msg-${msg.id}`}>
            <div className={`max-w-[80%] ${isPatient ? "items-end" : "items-start"} flex flex-col gap-1`}>
                <div className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    isPatient
                        ? "bg-primary text-white rounded-br-md"
                        : "bg-card border border-border rounded-bl-md"
                }`}>
                    {msg.body}
                </div>
                <div className="text-[10px] text-muted-foreground px-1">
                    {isPatient ? "You" : (msg.sender_name || coach?.name || "Coach")} · {timestampLong(msg.created_at)}
                </div>
            </div>
        </div>
    );
}


function NotEnrolledState() {
    return (
        <div className="max-w-md mx-auto text-center py-16" data-testid="patient-coach-not-enrolled">
            <div className="w-16 h-16 mx-auto rounded-full bg-secondary grid place-items-center mb-4">
                <Lock size={26} className="text-muted-foreground" />
            </div>
            <h2 className="font-display text-lg font-semibold">No compliance coach assigned</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Your doctor hasn&apos;t opted-in to Invisique™&apos;s compliance support service for your case.
                If you&apos;d like coaching help with wear-time and daily aligner questions, ask your doctor to enable it on your case.
            </p>
        </div>
    );
}


function PreEngagementState({ caseData }) {
    return (
        <div className="max-w-md mx-auto text-center py-16" data-testid="patient-coach-pre">
            <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 grid place-items-center mb-4">
                <Clock size={26} className="text-amber-700" />
            </div>
            <h2 className="font-display text-lg font-semibold">Your coach will be ready soon</h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Invisique™&apos;s compliance support is set up for your case. Your dedicated coach will introduce themselves here
                once your doctor confirms aligner fitment. {caseData?.code && (
                    <span className="block mt-2 text-[12px] text-muted-foreground/80">Case: <span className="font-mono">{caseData.code}</span></span>
                )}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary/40 border border-border text-[12px] text-muted-foreground">
                <Stethoscope size={13} />
                For now, message your doctor directly via &ldquo;Chat with doctor&rdquo;.
            </div>
        </div>
    );
}


/* ────────────────────────────────────────────────────────────────────
 * Dashboard banner — embeds anywhere a "your coach is here" tile fits.
 * Renders inline (no chrome) so the parent page controls placement.
 * ──────────────────────────────────────────────────────────────────── */
export function CoachBanner({ navigate }) {
    const [summary, setSummary] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/patient/me/compliance-thread/summary");
                if (!cancelled) setSummary(r.data);
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, []);

    if (!summary?.has_coach) return null;
    const unread = summary.unread_count || 0;
    const coach = summary.coach_name || "your coach";
    return (
        <button
            type="button"
            onClick={() => navigate?.("/patient/coach")}
            className="w-full text-left rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 px-5 py-4 hover:from-primary/15 hover:to-primary/10 transition group"
            data-testid="patient-coach-banner"
        >
            <div className="flex items-center gap-3">
                <div className="shrink-0 w-11 h-11 rounded-full bg-primary text-white grid place-items-center">
                    <Heart size={22} weight="fill" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-display text-sm font-semibold">Your CoE Compliance Coach is here to help</span>
                        {unread > 0 && (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-rose-500 text-white" data-testid="patient-coach-banner-unread">
                                {unread} new
                            </span>
                        )}
                    </div>
                    <div className="text-[12px] text-muted-foreground mt-0.5">
                        Chat with <span className="font-medium text-foreground">{coach}</span> for help with wear-time, aligner questions, and daily check-ins.
                    </div>
                </div>
                <ChatCircleDots size={20} className="text-primary group-hover:translate-x-0.5 transition" />
            </div>
        </button>
    );
}
